'use client';

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  EditableImage,
  EditableText,
  sanitizeLinkUrl,
  sanitizeMapUrl,
  sanitizeMediaUrl,
  sanitizePhoneHref,
  sanitizeWhatsAppHref,
  trackEvent,
  useEditMode,
  type ImageValue,
  type SiteRendererProps,
} from '@locusly/client-runtime';
import heroBgPng from '../source/static/6f58d92b-8208-4d8d-852c-f4f5a6974f57.png';
import logoPng from '../source/static/logo-transparent.png';
import ownerJpg from '../source/static/owner.jpg';
import type { MadeiraMobileMechanicContent } from './content.schema';
import defaultContent from './defaultContent';
import sourceStyles from './source-styles';
import { translate, type Lang } from './i18n';

type ImportedAsset = string | { src: string };
type LeadState = 'idle' | 'sending' | 'success' | 'error';

function assetUrl(asset: ImportedAsset) {
  return typeof asset === 'string' ? asset : asset.src;
}

const HERO_BG_URL = assetUrl(heroBgPng);
const LOGO_URL = assetUrl(logoPng);
const OWNER_URL = assetUrl(ownerJpg);
const SITE_ROOT_ID = 'mmm-site';
const SCOPE = `#${SITE_ROOT_ID}`;

function rewriteAssetUrls(css: string) {
  return css
    .replaceAll('./static/6f58d92b-8208-4d8d-852c-f4f5a6974f57.png', HERO_BG_URL)
    .replaceAll('../static/6f58d92b-8208-4d8d-852c-f4f5a6974f57.png', HERO_BG_URL)
    .replaceAll('./static/logo-transparent.png', LOGO_URL)
    .replaceAll('../static/logo-transparent.png', LOGO_URL)
    .replaceAll('./static/owner.jpg', OWNER_URL)
    .replaceAll('../static/owner.jpg', OWNER_URL);
}

function scopeOneSelector(selector: string): string {
  const match = selector.match(/^(\s*)([\s\S]*?)(\s*)$/);
  if (!match) return selector;
  const [, lead, core, trail] = match;
  if (!core) return selector;
  if (core.startsWith(SCOPE)) return selector;
  if (/^:root\b/.test(core)) return `${lead}${core.replace(/^:root/, SCOPE)}${trail}`;
  if (/^html\b/.test(core)) return `${lead}${core.replace(/^html/, SCOPE)}${trail}`;
  if (/^body\b/.test(core)) return `${lead}${core.replace(/^body/, SCOPE)}${trail}`;
  if (/^from\b|^to\b|^\d+%/.test(core)) return selector;
  return `${lead}${SCOPE} ${core}${trail}`;
}

function scopeStyles(styles: string): string {
  const out: string[] = [];
  let pos = 0;
  const len = styles.length;

  while (pos < len) {
    const ch = styles[pos];

    if (ch === '/' && styles[pos + 1] === '*') {
      const end = styles.indexOf('*/', pos + 2);
      const finish = end === -1 ? len : end + 2;
      out.push(styles.slice(pos, finish));
      pos = finish;
      continue;
    }

    if (ch === '@') {
      const nameMatch = styles.slice(pos).match(/^@([\w-]+)/);
      const name = nameMatch?.[1]?.toLowerCase() ?? '';
      let scan = pos;
      let inStr: string | null = null;
      while (scan < len) {
        const c = styles[scan];
        if (inStr) {
          if (c === '\\') { scan += 2; continue; }
          if (c === inStr) inStr = null;
        } else if (c === '"' || c === "'") {
          inStr = c;
        } else if (c === '{' || c === ';') {
          break;
        }
        scan++;
      }
      if (scan >= len || styles[scan] === ';') {
        const finish = scan < len ? scan + 1 : len;
        out.push(styles.slice(pos, finish));
        pos = finish;
        continue;
      }
      const prelude = styles.slice(pos, scan + 1);
      out.push(prelude);
      pos = scan + 1;
      const bodyStart = pos;
      let depth = 1;
      while (pos < len && depth > 0) {
        const cc = styles[pos];
        if (cc === '{') depth++;
        else if (cc === '}') { depth--; if (depth === 0) break; }
        pos++;
      }
      const body = styles.slice(bodyStart, pos);
      const nested = name === 'media' || name === 'supports' || name === 'container' || name === 'layer';
      out.push(nested ? scopeStyles(body) : body);
      if (pos < len) { out.push('}'); pos++; }
      continue;
    }

    if (ch === '}') { out.push('}'); pos++; continue; }

    let i = pos;
    let parens = 0;
    let brackets = 0;
    let inStr: string | null = null;
    while (i < len) {
      const c = styles[i];
      if (inStr) {
        if (c === '\\') { i += 2; continue; }
        if (c === inStr) inStr = null;
      } else if (c === '"' || c === "'") {
        inStr = c;
      } else if (c === '(') parens++;
      else if (c === ')') parens--;
      else if (c === '[') brackets++;
      else if (c === ']') brackets--;
      else if (parens === 0 && brackets === 0 && (c === '{' || c === '}')) {
        break;
      }
      i++;
    }
    if (i >= len) { out.push(styles.slice(pos)); pos = len; break; }
    if (styles[i] === '}') { out.push(styles.slice(pos, i)); pos = i; continue; }

    const selectorList = styles.slice(pos, i);
    const scoped = selectorList.split(',').map(scopeOneSelector).join(',');
    out.push(scoped);
    pos = i;

    out.push('{');
    pos++;
    const bodyStart = pos;
    let depth = 1;
    while (pos < len && depth > 0) {
      const cc = styles[pos];
      if (cc === '{') depth++;
      else if (cc === '}') { depth--; if (depth === 0) break; }
      pos++;
    }
    out.push(styles.slice(bodyStart, pos));
    if (pos < len) { out.push('}'); pos++; }
  }
  return out.join('');
}

const pageStyles = scopeStyles(rewriteAssetUrls(sourceStyles));

function mergeContent(
  content: Record<string, Record<string, unknown>> | undefined,
): MadeiraMobileMechanicContent {
  const incoming = (content ?? {}) as unknown as Partial<MadeiraMobileMechanicContent>;
  return {
    branding: { ...defaultContent.branding, ...(incoming.branding ?? {}) },
    nav: {
      ...defaultContent.nav,
      ...(incoming.nav ?? {}),
      items: incoming.nav?.items?.length
        ? incoming.nav.items
        : defaultContent.nav?.items ?? [],
    },
    hero: { ...defaultContent.hero, ...(incoming.hero ?? {}) },
    buttons: { ...defaultContent.buttons, ...(incoming.buttons ?? {}) },
    stats: { ...defaultContent.stats, ...(incoming.stats ?? {}) },
    services: {
      ...defaultContent.services,
      ...(incoming.services ?? {}),
      items: incoming.services?.items?.length
        ? incoming.services.items
            .map((item, i) => ({
              ...defaultContent.services.items[i],
              ...item,
              name: item.name || defaultContent.services.items[i]?.name || '',
              description: item.description || defaultContent.services.items[i]?.description || '',
            }))
            .filter(item => item.name || item.description)
        : defaultContent.services.items,
    },
    areas: {
      ...defaultContent.areas,
      ...(incoming.areas ?? {}),
      items: incoming.areas?.items?.length
        ? incoming.areas.items
        : defaultContent.areas.items,
    },
    reviews: {
      ...defaultContent.reviews,
      ...(incoming.reviews ?? {}),
      items: incoming.reviews?.items?.length
        ? incoming.reviews.items
        : defaultContent.reviews.items,
    },
    faq: {
      ...defaultContent.faq,
      ...(incoming.faq ?? {}),
      items: incoming.faq?.items?.length ? incoming.faq.items : defaultContent.faq.items,
    },
    contact: { ...defaultContent.contact, ...(incoming.contact ?? {}) },
    seo: { ...defaultContent.seo, ...(incoming.seo ?? {}) },
    tourists: { ...defaultContent.tourists, ...(incoming.tourists ?? {}) },
    sections: { ...defaultContent.sections, ...(incoming.sections ?? {}) },
  };
}

function getInitials(name: string) {
  return name
    .trim()
    .split(/\s+/)
    .map((part) => part[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function leadMessageForState(state: LeadState, lang: Lang) {
  if (state === 'success') return translate(lang, 'form-success');
  if (state === 'error') return translate(lang, 'form-error');
  return translate(lang, 'form-note');
}

const SERVICE_ICONS: Array<React.ReactNode> = [
  // 1. Engine Diagnostics -sun-rays
  <svg key="i1" className="svc-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
  </svg>,
  // 2. Oil & Fluid Service -cylinder
  <svg key="i2" className="svc-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4.03 3-9 3S3 13.66 3 12" />
    <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
  </svg>,
  // 3. Brake Service -target
  <svg key="i3" className="svc-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
  </svg>,
  // 4. Battery Replacement -battery
  <svg key="i4" className="svc-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <rect x="1" y="6" width="18" height="13" rx="2" />
    <path d="M19 10h2a2 2 0 012 2v2a2 2 0 01-2 2h-2" />
    <path d="M7 10h4M7 14h4" />
  </svg>,
  // 5. Air Conditioning -gauge
  <svg key="i5" className="svc-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <path d="M12 2a10 10 0 100 20A10 10 0 0012 2z" />
    <path d="M12 6v6l4 2" />
    <path d="M6 12H2M22 12h-4M12 2v2M12 20v2" />
  </svg>,
  // 6. Pre-Purchase Inspection -clipboard check
  <svg key="i6" className="svc-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <path d="M9 11l3 3L22 4" />
    <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
  </svg>,
];

function safeAnchorHref(value: string | undefined) {
  return sanitizeLinkUrl(value, {
    allowHash: true,
    allowRelative: true,
  }) ?? '#';
}

export default function MadeiraMobileMechanicRenderer({
  content,
  publicWriteToken,
  routePath,
}: SiteRendererProps) {
  const { editing } = useEditMode();
  const data = useMemo(() => mergeContent(content), [content]);
  const [leadState, setLeadState] = useState<LeadState>('idle');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [lang, setLang] = useState<Lang>(routePath?.startsWith('/pt') ? 'pt' : 'en');
  const t = (key: string, vars?: Record<string, string>) => translate(lang, key, vars);

  const heroImage = data.hero.heroImage as ImageValue | undefined;
  const logoImage = data.branding?.logo as ImageValue | undefined;
  const ownerImage = data.hero.ownerImage as ImageValue | undefined;
  const safeHeroImage = heroImage?.url
    ? { ...heroImage, url: sanitizeMediaUrl(heroImage.url) ?? HERO_BG_URL }
    : { url: HERO_BG_URL, alt: data.hero.businessName };
  const safeLogoImage = logoImage?.url
    ? { ...logoImage, url: sanitizeMediaUrl(logoImage.url) ?? LOGO_URL }
    : { url: LOGO_URL, alt: data.hero.businessName };
  const safeOwnerImage = ownerImage?.url
    ? { ...ownerImage, url: sanitizeMediaUrl(ownerImage.url) ?? OWNER_URL }
    : { url: OWNER_URL, alt: data.hero.ownerTitle ?? data.hero.businessName };
  const navItems = data.nav?.items ?? [];
  const logoHref = safeAnchorHref(data.branding?.logoHref);
  const heroBackground = `linear-gradient(135deg, rgba(6, 18, 32, 0.94) 0%, rgba(6, 18, 32, 0.78) 42%, rgba(6, 18, 32, 0.48) 100%), url("${safeHeroImage.url}")`;
  const phoneHref = sanitizePhoneHref(data.hero.phone);
  const whatsappHref = sanitizeWhatsAppHref(data.hero.whatsapp);
  const mapUrl = sanitizeMapUrl(data.contact.mapUrl) ?? sanitizeMapUrl(defaultContent.contact.mapUrl);
  const directionsHref = data.contact.address
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(data.contact.address)}`
    : null;

  useEffect(() => {
    setLang(routePath?.startsWith('/pt') ? 'pt' : 'en');
  }, [routePath]);

  useEffect(() => {
    trackEvent('page_view', { lang });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = data.hero.businessName;
    return () => {
      document.title = previousTitle;
    };
  }, [data.hero.businessName]);

  useEffect(() => {
    const previous = document.documentElement.lang;
    document.documentElement.lang = lang;
    return () => {
      document.documentElement.lang = previous;
    };
  }, [lang]);

  function closeMobile() {
    setMobileOpen(false);
  }

  function handlePhoneClick() {
    if (!editing) trackEvent('phone_click');
  }

  function handleWhatsAppClick() {
    if (!editing) trackEvent('whatsapp_click');
  }

  function handleCtaClick() {
    if (!editing) trackEvent('cta_click');
  }

  function handleDirectionsClick() {
    if (!editing) trackEvent('directions_click');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (editing || leadState === 'sending') return;

    const form = event.currentTarget;
    const formData = new FormData(form);
    const first = String(formData.get('firstName') ?? '').trim();
    const last = String(formData.get('lastName') ?? '').trim();
    const email = String(formData.get('email') ?? '').trim();
    const phone = String(formData.get('phone') ?? '').trim();
    const vehicle = String(formData.get('vehicle') ?? '').trim();
    const year = String(formData.get('year') ?? '').trim();
    const service = String(formData.get('service') ?? '').trim();
    const address = String(formData.get('address') ?? '').trim();
    const note = String(formData.get('message') ?? '').trim();
    const parts = [
      vehicle ? `Vehicle: ${vehicle}${year ? ` (${year})` : ''}` : year ? `Year: ${year}` : '',
      service ? `Service: ${service}` : '',
      address ? `Location: ${address}` : '',
      note,
    ].filter(Boolean);

    setLeadState('sending');
    try {
      const tokenRes = await fetch('/api/public-write-token', { cache: 'no-store' });
      if (!tokenRes.ok) throw new Error('token_fetch_failed');
      const { token: freshToken } = (await tokenRes.json()) as { token?: string };
      if (!freshToken) throw new Error('token_missing');
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          publicWriteToken: freshToken,
          source: 'contact_form',
          name: `${first} ${last}`.trim() || undefined,
          email: email || undefined,
          phone: phone || undefined,
          message: parts.join('\n') || undefined,
        }),
      });
      if (!response.ok) throw new Error('lead_submit_failed');
      trackEvent('lead_submitted', { source: 'contact_form' });
      form.reset();
      setLeadState('success');
    } catch {
      setLeadState('error');
    }
  }

  function preventEditNavigation(event: React.MouseEvent<HTMLAnchorElement>) {
    if (editing) event.preventDefault();
  }

  function handleMobileLinkClick(event: React.MouseEvent<HTMLAnchorElement>) {
    if (editing) {
      event.preventDefault();
      return;
    }
    closeMobile();
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: pageStyles }} />
      <style>{`
        #${SITE_ROOT_ID} .mmm-logo-edit {
          height: 100%;
          width: auto;
          max-width: 190px;
          object-fit: contain;
          display: block;
        }

        #${SITE_ROOT_ID} .footer-logo .mmm-logo-edit {
          height: 60px;
        }

        #${SITE_ROOT_ID} .mmm-hero-image-editor {
          margin-top: 1rem;
          max-width: 260px;
        }

        #${SITE_ROOT_ID} .mmm-hero-image-editor > span {
          display: block;
          color: rgba(255, 255, 255, 0.72);
          font-size: 0.76rem;
          margin-bottom: 0.45rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        #${SITE_ROOT_ID} .mmm-hero-image-preview {
          width: 100%;
          aspect-ratio: 16 / 9;
          object-fit: cover;
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.24);
        }

        #${SITE_ROOT_ID} .mmm-owner-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-top: 1.5rem;
          padding: 1rem;
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.04);
        }

        #${SITE_ROOT_ID} .mmm-owner-image {
          width: 84px;
          height: 84px;
          flex: 0 0 84px;
          border-radius: 8px;
          object-fit: cover;
        }

        #${SITE_ROOT_ID} .mmm-owner-card-content h3 {
          margin: 0 0 0.35rem;
          font-size: 1rem;
        }

        #${SITE_ROOT_ID} .mmm-owner-card-content p {
          margin: 0;
        }

        #${SITE_ROOT_ID} .hamburger { cursor: pointer; }

        @media (max-width: 768px) {
          #${SITE_ROOT_ID} .container {
            width: 100%;
            max-width: 100%;
            padding-left: 1rem;
            padding-right: 1rem;
          }

          #${SITE_ROOT_ID} nav {
            z-index: 240;
            padding: 0.65rem 0;
            background: rgba(7, 18, 39, 0.98);
            backdrop-filter: blur(14px);
          }

          #${SITE_ROOT_ID} .nav-inner {
            min-height: 58px;
            gap: 0.75rem;
          }

          #${SITE_ROOT_ID} .nav-logo .mmm-logo-edit {
            height: 48px;
            max-width: 150px;
          }

          #${SITE_ROOT_ID} .nav-right {
            gap: 0.55rem;
            margin-left: auto;
          }

          #${SITE_ROOT_ID} .lang-toggle {
            flex-shrink: 0;
          }

          #${SITE_ROOT_ID} .hamburger {
            display: inline-flex;
            width: 42px;
            height: 42px;
            align-items: center;
            justify-content: center;
            gap: 5px;
            border: 1px solid rgba(132, 154, 189, 0.34);
            border-radius: 8px;
            background: rgba(10, 24, 50, 0.72);
            position: relative;
            z-index: 230;
          }

          #${SITE_ROOT_ID} .hamburger span {
            width: 19px;
            height: 2px;
            border-radius: 999px;
          }

          #${SITE_ROOT_ID} .hamburger.active span:nth-child(1) {
            transform: translateY(7px) rotate(45deg);
          }

          #${SITE_ROOT_ID} .hamburger.active span:nth-child(2) {
            opacity: 0;
          }

          #${SITE_ROOT_ID} .hamburger.active span:nth-child(3) {
            transform: translateY(-7px) rotate(-45deg);
          }

          #${SITE_ROOT_ID} .mobile-nav {
            z-index: 210;
            justify-content: flex-start;
            align-items: stretch;
            gap: 0.35rem;
            overflow-y: auto;
            padding: 6rem 1rem 2rem;
            background:
              linear-gradient(180deg, rgba(7, 18, 39, 0.98), rgba(8, 22, 46, 0.98)),
              var(--deep);
          }

          #${SITE_ROOT_ID} .mobile-nav a {
            width: 100%;
            border-bottom: 1px solid rgba(132, 154, 189, 0.18);
            padding: 1rem 0.25rem;
            font-family: 'Barlow Condensed', sans-serif;
            font-size: 1.05rem;
            font-weight: 600;
            letter-spacing: 0.16em;
            line-height: 1.1;
            text-transform: uppercase;
          }

          #${SITE_ROOT_ID} section {
            padding: 4rem 0;
          }

          #${SITE_ROOT_ID} .section-head {
            margin-bottom: 2rem;
          }

          #${SITE_ROOT_ID} .section-head h2,
          #${SITE_ROOT_ID} .trust-left h2,
          #${SITE_ROOT_ID} .form-aside h2 {
            font-size: 2.1rem;
            line-height: 1.08;
          }

          #${SITE_ROOT_ID} .hero {
            min-height: auto;
            padding: 6.25rem 0 2.5rem;
            align-items: flex-start;
            background-position: 64% center !important;
            background-size: cover !important;
          }

          #${SITE_ROOT_ID} .hero-wrap {
            grid-template-columns: 1fr;
            gap: 1.25rem;
            align-items: start;
          }

          #${SITE_ROOT_ID} .hero-content {
            max-width: none;
          }

          #${SITE_ROOT_ID} .hero h1 {
            max-width: 11ch;
            font-size: clamp(2.35rem, 13vw, 3.1rem);
            line-height: 0.98;
            margin-bottom: 1rem;
          }

          #${SITE_ROOT_ID} .hero-tagline {
            max-width: 22rem;
            font-size: 1rem;
            line-height: 1.45;
            margin-bottom: 1.45rem;
          }

          #${SITE_ROOT_ID} .hero-ctas {
            display: grid;
            grid-template-columns: 1fr;
            gap: 0.75rem;
          }

          #${SITE_ROOT_ID} .hero-ctas .btn-primary,
          #${SITE_ROOT_ID} .hero-ctas .btn-ghost {
            width: 100%;
            min-height: 48px;
            justify-content: center;
            padding: 0.85rem 1rem;
            text-align: center;
          }

          #${SITE_ROOT_ID} .hero-card {
            width: 100%;
            margin: 0;
            padding: 1rem;
            border-radius: 10px;
          }

          #${SITE_ROOT_ID} .scroll-cue {
            display: none;
          }

          #${SITE_ROOT_ID} .stats-grid,
          #${SITE_ROOT_ID} .services-grid,
          #${SITE_ROOT_ID} .trust-layout,
          #${SITE_ROOT_ID} .trust-feats,
          #${SITE_ROOT_ID} .reviews-grid,
          #${SITE_ROOT_ID} .location-layout,
          #${SITE_ROOT_ID} .form-layout,
          #${SITE_ROOT_ID} .tourists-card {
            grid-template-columns: 1fr;
          }

          #${SITE_ROOT_ID} .svc {
            min-height: 0;
            padding: 1.15rem;
            display: grid;
            grid-template-columns: 44px 1fr;
            column-gap: 0.9rem;
            align-items: start;
          }

          #${SITE_ROOT_ID} .svc-num {
            top: 0.7rem;
            right: 0.8rem;
            font-size: 2rem;
            opacity: 0.42;
          }

          #${SITE_ROOT_ID} .svc-icon {
            grid-row: span 2;
            width: 34px;
            height: 34px;
            margin: 0.1rem 0 0;
          }

          #${SITE_ROOT_ID} .svc h3 {
            padding-right: 2.4rem;
            font-size: 1.18rem;
            line-height: 1.15;
            margin: 0 0 0.4rem;
          }

          #${SITE_ROOT_ID} .svc p,
          #${SITE_ROOT_ID} .trust-feat p {
            font-size: 0.9rem;
            line-height: 1.55;
          }

          #${SITE_ROOT_ID} .services-foot {
            grid-template-columns: 1fr;
            margin-top: 1rem;
          }

          #${SITE_ROOT_ID} .services-foot-note {
            padding: 1rem;
            align-items: flex-start;
          }

          #${SITE_ROOT_ID} .services-foot .btn-primary {
            width: calc(100% - 2rem);
            min-width: 0;
            margin: 0 1rem 1rem;
          }

          #${SITE_ROOT_ID} .trust {
            overflow: visible;
          }

          #${SITE_ROOT_ID} .trust-layout {
            gap: 1.6rem;
          }

          #${SITE_ROOT_ID} .trust-feat {
            min-height: 0;
            padding: 1rem;
            display: grid;
            grid-template-columns: 42px 1fr;
            gap: 0.85rem;
            align-items: start;
            text-align: left;
          }

          #${SITE_ROOT_ID} .trust-feat .feat-icon {
            width: 42px;
            height: 42px;
          }

          #${SITE_ROOT_ID} .review-body {
            min-height: 0;
          }
        }

        @media (max-width: 420px) {
          #${SITE_ROOT_ID} .nav-logo .mmm-logo-edit {
            height: 42px;
            max-width: 125px;
          }

          #${SITE_ROOT_ID} .lang-btn {
            padding: 0.3rem 0.48rem;
            font-size: 0.64rem;
          }

          #${SITE_ROOT_ID} .hamburger {
            width: 38px;
            height: 38px;
          }

          #${SITE_ROOT_ID} .hero {
            background-position: 68% center !important;
          }

          #${SITE_ROOT_ID} .hero h1 {
            font-size: 2.28rem;
          }
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .nav-links,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .nav-links,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .nav-links {
          display: none;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .hamburger,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hamburger,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hamburger {
          display: inline-flex;
          width: 42px;
          height: 42px;
          align-items: center;
          justify-content: center;
          gap: 5px;
          border: 1px solid rgba(132, 154, 189, 0.34);
          border-radius: 8px;
          background: rgba(10, 24, 50, 0.72);
          position: relative;
          z-index: 230;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .hamburger span,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hamburger span,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hamburger span {
          width: 19px;
          height: 2px;
          border-radius: 999px;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} nav,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} nav,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} nav {
          z-index: 240;
          padding: 0.65rem 0;
          background: rgba(7, 18, 39, 0.98);
          backdrop-filter: blur(14px);
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .nav-logo .mmm-logo-edit,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .nav-logo .mmm-logo-edit,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .nav-logo .mmm-logo-edit {
          height: 48px;
          max-width: 150px;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .mobile-nav,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .mobile-nav,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .mobile-nav {
          z-index: 210;
          justify-content: flex-start;
          align-items: stretch;
          gap: 0.35rem;
          overflow-y: auto;
          padding: 6rem 1rem 2rem;
          background:
            linear-gradient(180deg, rgba(7, 18, 39, 0.98), rgba(8, 22, 46, 0.98)),
            var(--deep);
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .mobile-nav a,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .mobile-nav a,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .mobile-nav a {
          width: 100%;
          border-bottom: 1px solid rgba(132, 154, 189, 0.18);
          padding: 1rem 0.25rem;
          font-family: 'Barlow Condensed', sans-serif;
          font-size: 1.05rem;
          font-weight: 600;
          letter-spacing: 0.16em;
          line-height: 1.1;
          text-transform: uppercase;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .container,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .container,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .container {
          width: 100%;
          max-width: 100%;
          padding-left: 1rem;
          padding-right: 1rem;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} section,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} section,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} section {
          padding: 4rem 0;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .hero,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero {
          min-height: auto;
          padding: 6.25rem 0 2.5rem;
          align-items: flex-start;
          background-position: 64% center !important;
          background-size: cover !important;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .hero-wrap,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero-wrap,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero-wrap {
          grid-template-columns: 1fr;
          gap: 1.25rem;
          align-items: start;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .hero-content,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero-content,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero-content {
          max-width: none;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .hero h1,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero h1,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero h1 {
          max-width: 11ch;
          font-size: clamp(2.35rem, 13vw, 3.1rem);
          line-height: 0.98;
          margin-bottom: 1rem;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .hero-tagline,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero-tagline,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero-tagline {
          max-width: 22rem;
          font-size: 1rem;
          line-height: 1.45;
          margin-bottom: 1.45rem;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .hero-ctas,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero-ctas,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero-ctas {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.75rem;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .hero-ctas .btn-primary,
        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .hero-ctas .btn-ghost,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero-ctas .btn-primary,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero-ctas .btn-ghost,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero-ctas .btn-primary,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero-ctas .btn-ghost {
          width: 100%;
          min-height: 48px;
          justify-content: center;
          padding: 0.85rem 1rem;
          text-align: center;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .hero-features,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero-features,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero-features,
        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .stats-grid,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .stats-grid,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .stats-grid,
        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .services-grid,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .services-grid,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .services-grid,
        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .trust-layout,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .trust-layout,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .trust-layout,
        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .trust-feats,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .trust-feats,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .trust-feats,
        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .reviews-grid,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .reviews-grid,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .reviews-grid,
        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .location-layout,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .location-layout,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .location-layout,
        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .form-layout,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .form-layout,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .form-layout,
        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .tourists-card,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .tourists-card,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .tourists-card {
          grid-template-columns: 1fr;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .hero-feat,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero-feat,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero-feat {
          border-right: 0;
          border-bottom: 1px solid rgba(132, 154, 189, 0.2);
          padding: 0.9rem 0.75rem;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .hero-feat:last-child,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero-feat:last-child,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero-feat:last-child {
          border-bottom: 0;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .hero-card,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero-card,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .hero-card {
          width: 100%;
          margin: 0;
          padding: 1rem;
          border-radius: 10px;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .stat,
        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .stat:nth-child(2),
        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .stat:nth-child(3),
        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .stat:nth-child(4),
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .stat,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .stat:nth-child(2),
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .stat:nth-child(3),
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .stat:nth-child(4),
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .stat,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .stat:nth-child(2),
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .stat:nth-child(3),
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .stat:nth-child(4) {
          border-right: 0;
          border-top: 1px solid rgba(132,154,189,0.2);
          padding: 1.05rem 0.85rem;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .stat:first-child,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .stat:first-child,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .stat:first-child {
          border-top: 0;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .svc,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .svc,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .svc {
          min-height: 0;
          padding: 1.15rem;
          display: grid;
          grid-template-columns: 44px 1fr;
          column-gap: 0.9rem;
          align-items: start;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .svc-icon,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .svc-icon,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .svc-icon {
          grid-row: span 2;
          width: 34px;
          height: 34px;
          margin: 0.1rem 0 0;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .svc h3,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .svc h3,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .svc h3 {
          padding-right: 2.4rem;
          font-size: 1.18rem;
          line-height: 1.15;
          margin: 0 0 0.4rem;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .services-foot,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .services-foot,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .services-foot {
          grid-template-columns: 1fr;
          margin-top: 1rem;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .services-foot-note,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .services-foot-note,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .services-foot-note {
          padding: 1rem;
          align-items: flex-start;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .services-foot .btn-primary,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .services-foot .btn-primary,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .services-foot .btn-primary {
          width: calc(100% - 2rem);
          min-width: 0;
          margin: 0 1rem 1rem;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .trust-layout,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .trust-layout,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .trust-layout {
          gap: 1.6rem;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .trust-feat,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .trust-feat,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .trust-feat {
          min-height: 0;
          padding: 1rem;
          display: grid;
          grid-template-columns: 42px 1fr;
          gap: 0.85rem;
          align-items: start;
          text-align: left;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .review-body,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .review-body,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .review-body {
          min-height: 0;
        }

        #${SITE_ROOT_ID} .hamburger {
          border: 0;
          font: inherit;
        }

        #${SITE_ROOT_ID} .hamburger-lines {
          display: inline-flex;
          flex-direction: column;
          gap: 4px;
        }

        #${SITE_ROOT_ID} .hamburger .hamburger-lines span {
          display: block;
          width: 18px;
          height: 2px;
          border-radius: 999px;
          background: currentColor;
          transition: transform 0.2s ease, opacity 0.2s ease;
        }

        #${SITE_ROOT_ID} .hamburger-label {
          display: inline-block;
        }

        #${SITE_ROOT_ID} .hamburger.active > .hamburger-lines,
        #${SITE_ROOT_ID} .hamburger.active > .hamburger-label {
          transform: none;
          opacity: 1;
        }

        @media (max-width: 768px) {
          #${SITE_ROOT_ID} .hamburger {
            color: var(--cream);
            font-family: 'Barlow Condensed', sans-serif;
            font-size: 0.76rem;
            font-weight: 700;
            letter-spacing: 0.12em;
            text-transform: uppercase;
          }

          #${SITE_ROOT_ID} .hamburger.active .hamburger-lines span:nth-child(1) {
            transform: translateY(6px) rotate(45deg);
          }

          #${SITE_ROOT_ID} .hamburger.active .hamburger-lines span:nth-child(2) {
            opacity: 0;
          }

          #${SITE_ROOT_ID} .hamburger.active .hamburger-lines span:nth-child(3) {
            transform: translateY(-6px) rotate(-45deg);
          }

          #${SITE_ROOT_ID} .mobile-nav {
            position: static;
            inset: auto;
            display: none;
            margin-top: 0.75rem;
            padding: 0.45rem;
            border: 1px solid rgba(132, 154, 189, 0.24);
            border-radius: 18px;
            background: rgba(10, 24, 50, 0.94);
            box-shadow: 0 20px 50px rgba(4, 10, 22, 0.34);
          }

          #${SITE_ROOT_ID} .mobile-nav.open {
            display: grid;
          }

          #${SITE_ROOT_ID} .mobile-nav a {
            border-bottom: 0;
            border-radius: 14px;
            padding: 0.88rem 1rem;
            color: var(--cream);
            font-family: 'Barlow Condensed', sans-serif;
            font-size: 0.9rem;
            font-weight: 700;
            letter-spacing: 0.12em;
            text-transform: uppercase;
          }

          #${SITE_ROOT_ID} .mobile-nav a:hover {
            background: rgba(232, 145, 90, 0.12);
            color: var(--copper);
          }
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .mobile-nav,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .mobile-nav,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .mobile-nav {
          position: static;
          inset: auto;
          display: none;
          margin-top: 0.75rem;
          padding: 0.45rem;
          border: 1px solid rgba(132, 154, 189, 0.24);
          border-radius: 18px;
          background: rgba(10, 24, 50, 0.94);
          box-shadow: 0 20px 50px rgba(4, 10, 22, 0.34);
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .mobile-nav.open,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .mobile-nav.open,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .mobile-nav.open {
          display: grid;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .mobile-nav a,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .mobile-nav a,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .mobile-nav a {
          border-bottom: 0;
          border-radius: 14px;
          padding: 0.88rem 1rem;
          color: var(--cream);
          font-size: 0.9rem;
          letter-spacing: 0.12em;
        }

        #${SITE_ROOT_ID} .mmm-menu-button {
          display: none;
          align-items: center;
          justify-content: center;
          gap: 0.55rem;
          min-height: 2.65rem;
          padding: 0 0.9rem;
          border: 1px solid rgba(132, 154, 189, 0.32);
          border-radius: 999px;
          background: rgba(10, 24, 50, 0.88);
          color: var(--cream);
          cursor: pointer;
          font-family: 'Barlow Condensed', sans-serif;
          font-size: 0.76rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          line-height: 1;
          text-transform: uppercase;
          transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
        }

        #${SITE_ROOT_ID} .mmm-menu-button:hover,
        #${SITE_ROOT_ID} .mmm-menu-button.active {
          border-color: rgba(232, 145, 90, 0.62);
          background: rgba(16, 37, 73, 0.96);
          color: var(--copper);
        }

        #${SITE_ROOT_ID} .mmm-menu-lines {
          display: inline-flex;
          width: 18px;
          flex-direction: column;
          gap: 4px;
          flex: 0 0 auto;
        }

        #${SITE_ROOT_ID} .mmm-menu-lines span {
          display: block;
          width: 18px;
          height: 2px;
          border-radius: 999px;
          background: currentColor;
          transform-origin: center;
          transition: transform 0.2s ease, opacity 0.2s ease;
        }

        #${SITE_ROOT_ID} .mmm-menu-button.active .mmm-menu-lines span:nth-child(1) {
          transform: translateY(6px) rotate(45deg);
        }

        #${SITE_ROOT_ID} .mmm-menu-button.active .mmm-menu-lines span:nth-child(2) {
          opacity: 0;
        }

        #${SITE_ROOT_ID} .mmm-menu-button.active .mmm-menu-lines span:nth-child(3) {
          transform: translateY(-6px) rotate(-45deg);
        }

        #${SITE_ROOT_ID} .mmm-menu-label {
          display: inline-block;
          width: auto;
          height: auto;
          opacity: 1;
          transform: none;
          white-space: nowrap;
        }

        @media (max-width: 768px) {
          #${SITE_ROOT_ID} .nav-right {
            display: flex !important;
            flex: 0 0 auto;
            margin-left: auto;
          }

          #${SITE_ROOT_ID} .nav-links {
            display: none !important;
          }

          #${SITE_ROOT_ID} .mmm-menu-button {
            display: inline-flex !important;
            visibility: visible !important;
            opacity: 1 !important;
          }
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .nav-right,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .nav-right,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .nav-right {
          display: flex !important;
          flex: 0 0 auto;
          margin-left: auto;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .nav-links,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .nav-links,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .nav-links {
          display: none !important;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .mmm-menu-button,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .mmm-menu-button,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .mmm-menu-button {
          display: inline-flex !important;
          visibility: visible !important;
          opacity: 1 !important;
        }
      `}</style>
      <div id={SITE_ROOT_ID}>
        <nav id="nav">
          <div className="container">
            <div className="nav-inner">
              <a className="nav-logo" href={logoHref} onClick={preventEditNavigation}>
                <EditableImage
                  path="branding.logo"
                  value={safeLogoImage}
                  className="mmm-logo-edit"
                  emptyLabel="Adicionar logotipo"
                />
              </a>
              <ul className="nav-links">
                {navItems.map((item, index) => (
                  <li key={index}>
                    <a href={safeAnchorHref(item.href)} onClick={preventEditNavigation}>
                      <EditableText path={`nav.items.${index}.label`} richText={false}>
                        {item.label}
                      </EditableText>
                    </a>
                  </li>
                ))}
              </ul>
              <div className="nav-right">
                <div className="lang-toggle" role="group" aria-label="Language">
                  {editing ? (
                    <>
                      <button
                        className={`lang-btn${lang === 'en' ? ' active' : ''}`}
                        type="button"
                        aria-pressed={lang === 'en'}
                        onClick={() => setLang('en')}
                      >EN</button>
                      <button
                        className={`lang-btn${lang === 'pt' ? ' active' : ''}`}
                        type="button"
                        aria-pressed={lang === 'pt'}
                        onClick={() => setLang('pt')}
                      >PT</button>
                    </>
                  ) : (
                    <>
                      <a
                        className={`lang-btn${lang === 'en' ? ' active' : ''}`}
                        href="/en"
                        onClick={() => { if (lang !== 'en') trackEvent('page_view', { lang: 'en' }); }}
                      >EN</a>
                      <a
                        className={`lang-btn${lang === 'pt' ? ' active' : ''}`}
                        href="/pt"
                        onClick={() => { if (lang !== 'pt') trackEvent('page_view', { lang: 'pt' }); }}
                      >PT</a>
                    </>
                  )}
                </div>
                <button
                  className={`mmm-menu-button${mobileOpen ? ' active' : ''}`}
                  id="ham"
                  aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={mobileOpen}
                  aria-controls="mobileNav"
                  type="button"
                  onClick={() => setMobileOpen((v) => !v)}
                >
                  <span className="mmm-menu-lines" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </span>
                  <span className="mmm-menu-label">{mobileOpen ? 'Close' : 'Menu'}</span>
                </button>
              </div>
            </div>
            <div className={`mobile-nav${mobileOpen ? ' open' : ''}`} id="mobileNav">
              {navItems.map((item, index) => (
                <a
                  href={safeAnchorHref(item.href)}
                  onClick={handleMobileLinkClick}
                  key={index}
                >
                  <EditableText path={`nav.items.${index}.label`} richText={false}>
                    {item.label}
                  </EditableText>
                </a>
              ))}
            </div>
          </div>
        </nav>

        <section
          className="hero"
          id="home"
          style={{
            backgroundImage: heroBackground,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }}
        >
          <div className="hero-bg" />
          <div className="hero-overlay" />
          <div className="hero-panel" />
          <svg
            className="hero-ghost"
            viewBox="0 0 900 340"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path d="M80 230 Q130 170 250 148 Q340 132 430 118 L545 112 Q630 112 680 130 L755 148 Q820 165 840 188 L858 210 L870 225 Q885 232 885 232 L885 255 Q875 268 852 268 L840 268 Q834 296 810 308 Q782 322 754 308 Q732 297 726 268 L230 268 Q224 296 200 308 Q172 322 144 308 Q122 297 116 268 L90 262 Q62 250 80 230Z" />
            <circle cx="172" cy="295" r="40" />
            <circle cx="172" cy="295" r="23" fill="var(--navy)" />
            <circle cx="778" cy="295" r="40" />
            <circle cx="778" cy="295" r="23" fill="var(--navy)" />
            <path d="M260 148 L300 90 Q320 70 360 68 L500 68 Q540 68 560 90 L590 148Z" opacity="0.6" />
          </svg>
          <div className="container hero-wrap">
            <div className="hero-content reveal in">
              <div className="hero-eyebrow reveal in">
                <EditableText path="sections.heroEyebrow" as="span" className="label">
                  {data.sections?.heroEyebrow ?? ''}
                </EditableText>
              </div>
              <EditableText path="hero.businessName" as="h1">
                {data.hero.businessName}
              </EditableText>
              <EditableText
                path={lang === 'pt' ? 'hero.taglinePt' : 'hero.tagline'}
                as="p"
                className="hero-tagline"
                multiline
              >
                {lang === 'pt' ? (data.hero.taglinePt || data.hero.tagline) : data.hero.tagline}
              </EditableText>
              <div className="hero-ctas">
                <a className="btn-primary" href="#contact" onClick={(e) => { preventEditNavigation(e); handleCtaClick(); }}>
                  <EditableText path="buttons.bookLabel" richText={false}>
                    {data.buttons.bookLabel}
                  </EditableText>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </a>
                <a className="btn-ghost" href={phoneHref ?? '#contact'} onClick={(e) => { preventEditNavigation(e); handlePhoneClick(); }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 012 1h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L6.91 8.9a16 16 0 006.12 6.12l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z" />
                  </svg>
                  <EditableText path="buttons.callLabel" richText={false}>
                    {data.buttons.callLabel}
                  </EditableText>
                </a>
              </div>
              <div className="hero-features reveal in">
                <div className="hero-feat">
                  <h4>{t('hero-feat1-h')}</h4>
                  <p>{t('hero-feat1-p')}</p>
                </div>
                <div className="hero-feat">
                  <h4>{t('hero-feat2-h')}</h4>
                  <p>{t('hero-feat2-p')}</p>
                </div>
                <div className="hero-feat">
                  <h4>{t('hero-feat3-h')}</h4>
                  <p>{t('hero-feat3-p')}</p>
                </div>
              </div>
              {editing ? (
                <div className="mmm-hero-image-editor">
                  <span>Hero image</span>
                  <EditableImage
                    path="hero.heroImage"
                    value={safeHeroImage}
                    className="mmm-hero-image-preview"
                    emptyLabel="Adicionar imagem hero"
                  />
                </div>
              ) : null}
            </div>
            <aside className="hero-card reveal in">
              <div className="stars">★★★★★</div>
              <p>{t('hero-card-trust')}</p>
              <strong>
                <EditableText path="stats.reviewCount" richText={false}>
                  {data.stats.reviewCount}
                </EditableText>{' '}
                {t('hero-card-reviews')}
              </strong>
            </aside>
          </div>
          <div className="scroll-cue">
            <span>{t('scroll')}</span>
            <div className="scroll-line" />
          </div>
        </section>

        <div className="stats-strip">
          <div className="container">
            <div className="stats-grid">
              {([
                ['stats.experience', data.stats.experience, 'stat-exp-sub'],
                ['stats.carsServiced', data.stats.carsServiced, 'stat-cars-sub'],
                ['stats.rating', data.stats.rating, 'stat-rating-sub'],
                ['stats.responseTime', data.stats.responseTime, 'stat-resp-sub'],
              ] satisfies Array<[string, string, string]>).map(([path, value, labelKey]) => (
                <div className="stat reveal in" key={path}>
                  <EditableText path={path} as="div" className="stat-num" richText={false}>
                    {value}
                  </EditableText>
                  <div className="stat-lbl">{t(labelKey)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <section className="services" id="services">
          <div className="container">
            <div className="section-head reveal in">
              <span className="label">{t('sec-svc-label')}</span>
              <div className="copper-line" />
              <EditableText path="sections.servicesHeading" as="h2">
                {data.sections?.servicesHeading ?? t('sec-svc-h2')}
              </EditableText>
            </div>
            <div className="services-grid reveal in">
              {data.services.items.map((service, index) => (
                <div className="svc" key={index}>
                  <span className="svc-num">{String(index + 1).padStart(2, '0')}</span>
                  {SERVICE_ICONS[index] ?? null}
                  <EditableText path={`services.items.${index}.name`} as="h3">
                    {service.name}
                  </EditableText>
                  <EditableText path={`services.items.${index}.description`} as="p" multiline>
                    {service.description}
                  </EditableText>
                </div>
              ))}
            </div>
            <div className="services-foot reveal in">
              <div className="services-foot-note">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ color: 'var(--copper)' }} aria-hidden="true">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
                <div>
                  <strong>{t('svc-foot-title')}</strong>
                  <span>{t('svc-foot-sub')}</span>
                </div>
              </div>
              <a className="btn-primary" href="#contact" onClick={(e) => { preventEditNavigation(e); handleCtaClick(); }}>
                <EditableText path="buttons.bookLabel" richText={false}>
                  {data.buttons.bookLabel}
                </EditableText>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>
        </section>

        <section className="trust" id="about">
          <div className="trust-ring trust-ring-1" aria-hidden="true" />
          <div className="trust-ring trust-ring-2" aria-hidden="true" />
          <div className="container">
            <div className="trust-layout">
              <div className="trust-left reveal in">
                <span className="label">{t('trust-label')}</span>
                <div className="copper-line" />
                <h2 dangerouslySetInnerHTML={{ __html: t('trust-h2') }} />
                <EditableText
                  path={lang === 'pt' ? 'hero.subtitlePt' : 'hero.subtitle'}
                  as="p"
                  multiline
                  style={{ marginTop: '1.25rem' }}
                >
                  {lang === 'pt' ? (data.hero.subtitlePt || data.hero.subtitle) : data.hero.subtitle}
                </EditableText>
                <div className="mmm-owner-card">
                  <EditableImage
                    path="hero.ownerImage"
                    value={safeOwnerImage}
                    className="mmm-owner-image"
                    emptyLabel="Adicionar foto"
                  />
                  <div className="mmm-owner-card-content">
                    <EditableText path="hero.ownerTitle" as="h3">
                      {data.hero.ownerTitle ?? ''}
                    </EditableText>
                    <EditableText path="hero.ownerSubtitle" as="p">
                      {data.hero.ownerSubtitle ?? ''}
                    </EditableText>
                  </div>
                </div>
              </div>
              <div className="trust-feats reveal in">
                <div className="trust-feat">
                  <div className="feat-icon" aria-hidden="true">
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                  </div>
                  <div>
                    <h4>{t('feat1-h')}</h4>
                    <p>{t('feat1-p')}</p>
                  </div>
                </div>
                <div className="trust-feat">
                  <div className="feat-icon" aria-hidden="true">
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                  </div>
                  <div>
                    <h4>{t('feat2-h')}</h4>
                    <p>{t('feat2-p')}</p>
                  </div>
                </div>
                <div className="trust-feat">
                  <div className="feat-icon" aria-hidden="true">
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <line x1="12" y1="1" x2="12" y2="23" />
                      <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                    </svg>
                  </div>
                  <div>
                    <h4>{t('feat3-h')}</h4>
                    <p>{t('feat3-p')}</p>
                  </div>
                </div>
                <div className="trust-feat">
                  <div className="feat-icon" aria-hidden="true">
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div>
                    <h4>{t('feat4-h')}</h4>
                    <p>{t('feat4-p')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="reviews" id="reviews">
          <div className="container">
            <div className="section-head reveal in">
              <span className="label">{t('sec-rev-label')}</span>
              <div className="copper-line" />
              <EditableText path="sections.reviewsHeading" as="h2">
                {data.sections?.reviewsHeading ?? 'Reviews'}
              </EditableText>
            </div>
            <div className="reviews-grid" id="reviewsGrid">
              {data.reviews.items.map((review, index) => (
                <div className="review reveal in" key={index}>
                  <EditableText path={`reviews.items.${index}.body`} as="p" className="review-body" multiline>
                    {review.body}
                  </EditableText>
                  <div className="review-foot">
                    <div className="avatar">{getInitials(review.author || 'MM')}</div>
                    <div className="reviewer">
                      <EditableText path={`reviews.items.${index}.author`} as="strong">
                        {review.author}
                      </EditableText>
                      <span>{t('review-verified')}</span>
                    </div>
                    <span className="review-src">{t('review-src-prefix')} {'★'.repeat(Number(review.rating) || 5)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="areas" id="areas">
          <div className="container">
            <div className="section-head reveal in">
              <span className="label">{t('sec-areas-label')}</span>
              <div className="copper-line" />
              <EditableText path="sections.areasHeading" as="h2">
                {data.sections?.areasHeading ?? 'Areas'}
              </EditableText>
              <EditableText path="areas.intro" as="p" multiline>
                {data.areas.intro ?? ''}
              </EditableText>
            </div>
            <div className="areas-grid reveal in">
              {data.areas.items.map((area, index) => (
                <div className="area-card" key={index}>
                  <EditableText path={`areas.items.${index}.name`} as="strong">
                    {area.name}
                  </EditableText>
                </div>
              ))}
            </div>
            <p className="areas-note reveal in">{t('sec-areas-note', { phone: data.hero.phone })}</p>
          </div>
        </section>

        <section className="tourists" id="tourists">
          <div className="container">
            <div className="tourists-card reveal in">
              <div>
                <span className="label">{t('sec-tour-label')}</span>
                <div className="copper-line" />
                <EditableText path={lang === 'pt' ? 'tourists.headingPt' : 'tourists.heading'} as="h2">
                  {lang === 'pt' ? (data.tourists?.headingPt || data.tourists?.heading) : data.tourists?.heading}
                </EditableText>
                <EditableText path={lang === 'pt' ? 'tourists.bodyPt' : 'tourists.body'} as="p" multiline>
                  {lang === 'pt' ? (data.tourists?.bodyPt || data.tourists?.body) : data.tourists?.body}
                </EditableText>
                <div className="hero-ctas" style={{ marginTop: '1.75rem' }}>
                  <a
                    className="btn-primary"
                    href={phoneHref ?? '#contact'}
                    onClick={(e) => { preventEditNavigation(e); handlePhoneClick(); }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 012 1h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L6.91 8.9a16 16 0 006.12 6.12l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z" />
                    </svg>
                    <EditableText path="buttons.emergencyLabel" richText={false}>
                      {data.buttons.emergencyLabel}
                    </EditableText>
                  </a>
                  {whatsappHref && (
                    <a className="btn-ghost" href={whatsappHref} onClick={(e) => { preventEditNavigation(e); handleWhatsAppClick(); }}>
                      <EditableText path="buttons.whatsappLabel" richText={false}>
                        {data.buttons.whatsappLabel}
                      </EditableText>
                    </a>
                  )}
                </div>
              </div>
              <div className="tourists-feats">
                <div className="tourists-feat">
                  <div className="feat-icon" aria-hidden="true">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
                    </svg>
                  </div>
                  <div>
                    <EditableText path={lang === 'pt' ? 'tourists.feat1TitlePt' : 'tourists.feat1Title'} as="strong">
                      {lang === 'pt' ? (data.tourists?.feat1TitlePt || data.tourists?.feat1Title) : data.tourists?.feat1Title}
                    </EditableText>
                    <EditableText path={lang === 'pt' ? 'tourists.feat1BodyPt' : 'tourists.feat1Body'} as="p" multiline>
                      {lang === 'pt' ? (data.tourists?.feat1BodyPt || data.tourists?.feat1Body) : data.tourists?.feat1Body}
                    </EditableText>
                  </div>
                </div>
                <div className="tourists-feat">
                  <div className="feat-icon" aria-hidden="true">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M3 12h18M12 3a15 15 0 010 18M12 3a15 15 0 000 18" />
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                  </div>
                  <div>
                    <EditableText path={lang === 'pt' ? 'tourists.feat2TitlePt' : 'tourists.feat2Title'} as="strong">
                      {lang === 'pt' ? (data.tourists?.feat2TitlePt || data.tourists?.feat2Title) : data.tourists?.feat2Title}
                    </EditableText>
                    <EditableText path={lang === 'pt' ? 'tourists.feat2BodyPt' : 'tourists.feat2Body'} as="p" multiline>
                      {lang === 'pt' ? (data.tourists?.feat2BodyPt || data.tourists?.feat2Body) : data.tourists?.feat2Body}
                    </EditableText>
                  </div>
                </div>
                <div className="tourists-feat">
                  <div className="feat-icon" aria-hidden="true">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div>
                    <EditableText path={lang === 'pt' ? 'tourists.feat3TitlePt' : 'tourists.feat3Title'} as="strong">
                      {lang === 'pt' ? (data.tourists?.feat3TitlePt || data.tourists?.feat3Title) : data.tourists?.feat3Title}
                    </EditableText>
                    <EditableText path={lang === 'pt' ? 'tourists.feat3BodyPt' : 'tourists.feat3Body'} as="p" multiline>
                      {lang === 'pt' ? (data.tourists?.feat3BodyPt || data.tourists?.feat3Body) : data.tourists?.feat3Body}
                    </EditableText>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="location" id="location">
          <div className="container">
            <div className="section-head reveal in">
              <span className="label">{t('sec-loc-label')}</span>
              <div className="copper-line" />
              <EditableText path="sections.locationHeading" as="h2">
                {data.sections?.locationHeading ?? t('sec-loc-h2')}
              </EditableText>
            </div>
            <div className="location-layout">
              <div className="reveal in">
                <div className="location-card">
                  <div className="contact-row">
                    <div className="contact-ico" aria-hidden="true">
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                    </div>
                    <div>
                      <strong>{t('loc-base')}</strong>
                      <EditableText path="contact.address" as="p" multiline>
                        {data.contact.address}
                      </EditableText>
                      {directionsHref && (
                        <a
                          href={directionsHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => { preventEditNavigation(e); handleDirectionsClick(); }}
                        >
                          {t('loc-directions')}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="contact-row">
                    <div className="contact-ico" aria-hidden="true">
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 012 1h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L6.91 8.9a16 16 0 006.12 6.12l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z" />
                      </svg>
                    </div>
                    <div>
                      <strong>{t('loc-phone')}</strong>
                      <a href={phoneHref ?? '#contact'} onClick={(e) => { preventEditNavigation(e); handlePhoneClick(); }}>
                        <EditableText path="hero.phone" richText={false}>
                          {data.hero.phone}
                        </EditableText>
                      </a>
                    </div>
                  </div>
                  {data.contact.email && (
                    <div className="contact-row">
                      <div className="contact-ico" aria-hidden="true">
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                          <polyline points="22,6 12,13 2,6" />
                        </svg>
                      </div>
                      <div>
                        <strong>{t('loc-email')}</strong>
                        <EditableText path="contact.email" richText={false}>
                          {data.contact.email}
                        </EditableText>
                      </div>
                    </div>
                  )}
                  {data.contact.paymentMethods && (
                    <div className="contact-row">
                      <div className="contact-ico" aria-hidden="true">
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                          <line x1="1" y1="10" x2="23" y2="10" />
                        </svg>
                      </div>
                      <div>
                        <strong>{t('loc-payment')}</strong>
                        <EditableText path="contact.paymentMethods" as="p" multiline>
                          {data.contact.paymentMethods}
                        </EditableText>
                      </div>
                    </div>
                  )}
                  <div className="contact-row">
                    <div className="contact-ico" aria-hidden="true">
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </div>
                    <div>
                      <strong>{t('loc-hours')}</strong>
                      <EditableText path="contact.hours" as="p" multiline>
                        {data.contact.hours}
                      </EditableText>
                    </div>
                  </div>
                </div>
              </div>
              {mapUrl && (
                <div className="map-wrap reveal in">
                  <iframe title="Madeira Mobile Mechanic map" src={mapUrl} loading="lazy" />
                  <a
                    className="btn-ghost"
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(data.contact.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent('directions_click')}
                    style={{ marginTop: '0.75rem', display: 'inline-flex' }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    {t('loc-directions')}
                  </a>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="faq" id="faq">
          <div className="container">
            <div className="section-head reveal in">
              <span className="label">{t('sec-faq-label')}</span>
              <div className="copper-line" />
              <EditableText path="sections.faqHeading" as="h2">
                {data.sections?.faqHeading ?? 'FAQ'}
              </EditableText>
            </div>
            <div className="faq-list reveal in">
              {data.faq.items.map((item, index) => (
                <details className="faq-item" key={index}>
                  <EditableText path={`faq.items.${index}.question`} as="summary">
                    {item.question}
                  </EditableText>
                  <EditableText path={`faq.items.${index}.answer`} as="div" className="faq-answer" multiline>
                    {item.answer}
                  </EditableText>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="contact-sec" id="contact">
          <div className="container">
            <div className="form-layout">
              <div className="form-aside reveal in">
                <span className="label">{t('sec-form-label')}</span>
                <div className="copper-line" />
                <EditableText path="sections.contactHeading" as="h2">
                  {data.sections?.contactHeading ?? 'Contact'}
                </EditableText>
                <p>{t('sec-form-p')}</p>
                <div className="call-box">
                  <span className="label">{t('prefer-call')}</span>
                  <a href={phoneHref ?? '#contact'} onClick={(e) => { preventEditNavigation(e); handlePhoneClick(); }}>
                    <EditableText path="hero.phone" richText={false}>
                      {data.hero.phone}
                    </EditableText>
                  </a>
                  <EditableText path="contact.hours" as="small" multiline>
                    {data.contact.hours}
                  </EditableText>
                </div>
                {whatsappHref && (
                  <a className="btn-ghost" href={whatsappHref} onClick={(e) => { preventEditNavigation(e); handleWhatsAppClick(); }}>
                    <EditableText path="buttons.whatsappLabel" richText={false}>
                      {data.buttons.whatsappLabel}
                    </EditableText>
                  </a>
                )}
              </div>

              <div className="reveal in">
                <form id="bookForm" onSubmit={handleSubmit}>
                  <div className="form-row2">
                    <div className="form-group">
                      <label>{t('lbl-fname')}</label>
                      <input name="firstName" type="text" required placeholder="Joao" disabled={editing} />
                    </div>
                    <div className="form-group">
                      <label>{t('lbl-lname')}</label>
                      <input name="lastName" type="text" required placeholder="Ferreira" disabled={editing} />
                    </div>
                  </div>
                  <div className="form-row2">
                    <div className="form-group">
                      <label>{t('lbl-email')}</label>
                      <input name="email" type="email" placeholder="you@example.com" disabled={editing} />
                    </div>
                    <div className="form-group">
                      <label>{t('lbl-phone')}</label>
                      <input name="phone" type="tel" placeholder={data.hero.phone} disabled={editing} />
                    </div>
                  </div>
                  <div className="form-row2">
                    <div className="form-group">
                      <label>{t('lbl-vehicle')}</label>
                      <input name="vehicle" type="text" placeholder="Volkswagen Golf" disabled={editing} />
                    </div>
                    <div className="form-group">
                      <label>{t('lbl-year')}</label>
                      <input name="year" type="text" placeholder="2019" disabled={editing} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{t('lbl-service')}</label>
                    <select name="service" disabled={editing}>
                      <option value="">{t('opt-select')}</option>
                      {data.services.items.map((service, index) => (
                        <option value={service.name} key={index}>{service.name}</option>
                      ))}
                      <option value="Other">{t('opt-other')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('lbl-address')}</label>
                    <input name="address" type="text" placeholder="Funchal" disabled={editing} />
                  </div>
                  <div className="form-group">
                    <label>{t('lbl-msg')}</label>
                    <textarea name="message" placeholder="..." disabled={editing} />
                  </div>
                  <button
                    type="submit"
                    className="btn-primary"
                    style={{ width: '100%', justifyContent: 'center', padding: '1.1rem' }}
                    disabled={editing || leadState === 'sending'}
                  >
                    {leadState === 'sending' ? t('btn-sending') : t('btn-send')}
                  </button>
                  <p className="form-note">{leadMessageForState(leadState, lang)}</p>
                </form>
              </div>
            </div>
          </div>
        </section>

        <footer>
          <div className="container">
            <div className="footer-inner">
              <div className="footer-logo">
                <EditableImage
                  path="branding.logo"
                  value={safeLogoImage}
                  className="mmm-logo-edit"
                  emptyLabel="Adicionar logotipo"
                />
              </div>
              <ul className="footer-links">
                {navItems.map((item, index) => (
                  <li key={index}>
                    <a href={safeAnchorHref(item.href)} onClick={preventEditNavigation}>
                      <EditableText path={`nav.items.${index}.label`} richText={false}>
                        {item.label}
                      </EditableText>
                    </a>
                  </li>
                ))}
              </ul>
            <p className="footer-copy">
              © {new Date().getFullYear()}{' '}
              <EditableText path="hero.businessName" richText={false} as="span">
                {data.hero.businessName}
              </EditableText>. {t('footer-rights')}
            </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
